import { useEffect, useRef, type CSSProperties, type ReactElement } from 'react';
import {
  BlockTypeSelect,
  BoldItalicUnderlineToggles,
  CodeToggle,
  CreateLink,
  InsertThematicBreak,
  ListsToggle,
  MDXEditor,
  type MDXEditorMethods,
  UndoRedo,
  headingsPlugin,
  linkDialogPlugin,
  linkPlugin,
  listsPlugin,
  markdownShortcutPlugin,
  quotePlugin,
  Separator,
  thematicBreakPlugin,
  toolbarPlugin,
  type IconKey,
  type Translation,
} from '@mdxeditor/editor';
import '@mdxeditor/editor/style.css';
import {
  Bold,
  ChevronDown,
  Code,
  ExternalLink,
  Italic,
  Link2,
  List,
  ListChecks,
  ListOrdered,
  Minus,
  Redo2,
  SquarePen,
  Trash2,
  Underline,
  Undo2,
  Unlink2,
  X,
} from 'lucide-react';
import { MarkdownPreview } from '@/lib/markdown';

/* Иконки тулбара — lucide, как во всей админке (замена штатных
   material-иконок MDXEditor через prop iconComponentFor) */
const TOOLBAR_ICONS: Partial<Record<IconKey, ReactElement>> = {
  undo: <Undo2 size={16} />,
  redo: <Redo2 size={16} />,
  format_bold: <Bold size={16} />,
  format_italic: <Italic size={16} />,
  format_underlined: <Underline size={16} />,
  code: <Code size={16} />,
  format_list_bulleted: <List size={16} />,
  format_list_numbered: <ListOrdered size={16} />,
  format_list_checked: <ListChecks size={16} />,
  link: <Link2 size={16} />,
  link_off: <Unlink2 size={16} />,
  open_in_new: <ExternalLink size={16} />,
  edit: <SquarePen size={16} />,
  close: <X size={16} />,
  delete_small: <Trash2 size={16} />,
  delete_big: <Trash2 size={16} />,
  horizontal_rule: <Minus size={16} />,
  arrow_drop_down: <ChevronDown size={16} />,
};

/** Русские подписи тулбара и диалогов (встроенной локализации нет) */
const RU_STRINGS: Record<string, string> = {
  'toolbar.undo': 'Отменить ({{shortcut}})',
  'toolbar.redo': 'Повторить ({{shortcut}})',
  'toolbar.bold': 'Полужирный',
  'toolbar.removeBold': 'Снять полужирный',
  'toolbar.italic': 'Курсив',
  'toolbar.removeItalic': 'Снять курсив',
  'toolbar.underline': 'Подчёркнутый',
  'toolbar.removeUnderline': 'Снять подчёркивание',
  'toolbar.inlineCode': 'Код',
  'toolbar.removeInlineCode': 'Снять код',
  'toolbar.bulletedList': 'Список',
  'toolbar.numberedList': 'Нумерованный список',
  'toolbar.checkList': 'Чек-лист',
  'toolbar.link': 'Вставить ссылку',
  'toolbar.thematicBreak': 'Разделитель',
  'toolbar.blockTypes.paragraph': 'Абзац',
  'toolbar.blockTypes.heading': 'Заголовок {{level}}',
  'toolbar.blockTypes.quote': 'Цитата',
  /* Плейсхолдер селекта типа блока (до первого клика в текст тип блока
     ещё не вычислен) — показываем «Абзац»: до фокуса это и есть правда
     для курсора по умолчанию */
  'toolbar.blockTypeSelect.placeholder': 'Абзац',
  'toolbar.blockTypeSelect.selectBlockTypeTooltip': 'Тип блока',
  'createLink.title': 'Ссылка',
  'createLink.titleTooltip': 'Вставить ссылку',
  'createLink.url': 'URL',
  'createLink.urlPlaceholder': 'https://…',
  'createLink.text': 'Текст',
  'createLink.textTooltip': 'Текст ссылки',
  'createLink.cancelTooltip': 'Отмена',
  'createLink.saveTooltip': 'Сохранить',
  'dialogControls.cancel': 'Отмена',
  'dialogControls.save': 'Сохранить',
  'linkPreview.edit': 'Изменить',
  'linkPreview.open': 'Открыть',
  'linkPreview.remove': 'Удалить ссылку',
  'linkPreview.copyToClipboard': 'Копировать',
  'linkPreview.copied': 'Скопировано',
};

const translate: Translation = (key, defaultValue, interpolations) => {
  let text = RU_STRINGS[key] ?? defaultValue;
  if (interpolations) {
    for (const [name, value] of Object.entries(interpolations)) {
      text = text.replaceAll(`{{${name}}}`, String(value));
    }
  }
  return text;
};

/* Только то, что умеют рендерить мобильное приложение и MarkdownPreview:
   без картинок (фото шаблона отдельно), таблиц и task-списков.
   Блочный код и исходник-markdown (CodeMirror) не включаются по весу */
const PLUGINS = [
  headingsPlugin(),
  listsPlugin(),
  quotePlugin(),
  thematicBreakPlugin(),
  linkPlugin(),
  linkDialogPlugin(),
  markdownShortcutPlugin(),
  toolbarPlugin({
    toolbarContents: () => (
      <>
        <BoldItalicUnderlineToggles options={['Bold', 'Italic']} />
        <CodeToggle />
        <Separator />
        <BlockTypeSelect />
        <ListsToggle options={['bullet', 'number']} />
        <Separator />
        <CreateLink />
        <InsertThematicBreak />
        <Separator />
        <UndoRedo />
      </>
    ),
  }),
];

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  minHeight?: number;
  readOnly?: boolean;
}

/**
 * WYSIWYG-редактор инструкции на @mdxeditor/editor (Lexical): правится
 * сразу отформатированный текст, markdown — под капотом, ровно тот формат,
 * который читает мобильное приложение. Кнопки тулбара — штатные (состояние
 * формата живёт внутри редактора), но иконки заменены на lucide, тултипы —
 * русские, скин — на дизайн-токены админки (index.css). В readOnly —
 * готовый MarkdownPreview простым текстом, без рамки.
 */
export function MarkdownEditor({
  value,
  onChange,
  label,
  placeholder = '',
  minHeight = 120,
  readOnly = false,
}: MarkdownEditorProps) {
  const editorRef = useRef<MDXEditorMethods>(null);
  // Последний markdown, пришедший из редактора: чтобы не гонять его же
  // обратно в setMarkdown (сбрасывал бы курсор и историю правок)
  const lastEmitted = useRef(value);

  // Внешние сбросы значения (отмена правок, смена шаблона) → в редактор
  useEffect(() => {
    if (!readOnly && value !== lastEmitted.current) {
      lastEmitted.current = value;
      editorRef.current?.setMarkdown(value);
    }
  }, [value, readOnly]);

  return (
    <div
      className="mdx-field flex w-full flex-col gap-1.5"
      style={{ '--mdx-min-height': `${minHeight}px` } as CSSProperties}
    >
      {label && <span className="text-sm font-medium text-foreground">{label}</span>}
      {readOnly ? (
        // Просмотр — готовый MarkdownPreview простым текстом, без рамки
        <div className="text-sm text-foreground">
          {value.trim() ? (
            <MarkdownPreview source={value} />
          ) : (
            <span className="text-field-placeholder">—</span>
          )}
        </div>
      ) : (
        /* Secondary-вариант как у HeroUI-инпутов в карточках: фон --default, без тени */
        <div className="rounded-[var(--radius)] border border-field-border bg-default transition-colors focus-within:border-focus">
          <MDXEditor
            ref={editorRef}
            markdown={value}
            onChange={(markdown) => {
              lastEmitted.current = markdown;
              onChange(markdown);
            }}
            placeholder={placeholder}
            translation={translate}
            iconComponentFor={(name) => TOOLBAR_ICONS[name] ?? <Minus size={16} />}
            contentEditableClassName="mdx-content"
            plugins={PLUGINS}
          />
        </div>
      )}
    </div>
  );
}
